import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { usePesaParcours } from '../../stores/usePesaParcours';

const WorkspaceHeader = ({ isAssetsOpen, isNavOpen }) => {
  const { activeInvestigation, courseData, activeItem } = usePesaParcours();
  const { darkMode } = useTheme();
  const location = useLocation();

  const isPreview = new URLSearchParams(location.search).get('preview') === 'true';
  const isVisible = isNavOpen || isAssetsOpen;

  const activeTitle = activeItem?.data?.titre || 
    courseData?.modules
      ?.flatMap(m => m.granules)
      .find(g => g.id === activeInvestigation?.granuleId)?.titre || 
    null;

  const nodeId = activeItem?.id || activeInvestigation?.granuleId || null;

  if (!isVisible) return null;

  return (
    <div className={`h-10 shrink-0 border-b flex items-center justify-between transition-all duration-500 ${
      darkMode ? 'border-white/5 bg-black/40' : 'border-blue-100 bg-white/50'
    } backdrop-blur-md relative z-40 overflow-hidden`}>
      
      {/* ═══ BACKGROUND DECOR : SCANLINE ═══ */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-20">
        <div className={`absolute inset-0 bg-[linear-gradient(to_right,transparent_0%,rgba(59,130,246,0.1)_50%,transparent_100%)] w-[200%] animate-[shimmer_8s_infinite_linear]`} />
      </div>

      {/* ═══ GAUCHE : Status Session ═══ */}
      <div className={`flex items-center gap-5 h-full px-6 border-r transition-colors ${
        darkMode ? 'border-white/5' : 'border-blue-100'
      }`}>
        {/* Glow dynamique basé sur le mode */}
        <div className={`flex items-center gap-3 relative`}>
           <div className={`w-1 h-1 rounded-full animate-pulse ${
            isPreview ? 'bg-emerald-500 shadow-[0_0_10px_#10b981]' : 'bg-blue-500 shadow-[0_0_10px_#3b82f6]'
          }`} />
          <span className={`text-[8px] font-mono font-black tracking-[0.25em] uppercase ${
            isPreview ? 'text-emerald-500' : darkMode ? 'text-blue-400/70' : 'text-blue-600'
          }`}>
            {isPreview ? 'Sim_Mode::Active' : 'Neural_Link::Stable'}
          </span>
        </div>

        {/* Node ID avec style "Terminal" */}
        {nodeId && (
          <div className="flex items-center gap-2 group">
            <span className="text-[10px] font-mono text-slate-500 opacity-50">PCAID:</span>
            <span className={`text-[11px] font-mono px-1.5 py-0.5 rounded bg-white/5 border border-white/5 ${
              darkMode ? 'text-blue-300/40' : 'text-blue-500/60'
            }`}>
              {nodeId.slice(-12).toUpperCase()}
            </span>
          </div>
        )}
      </div>

      {/* ═══ CENTRE : Micro telemetry (Optionnel) ═══ */}
      <div className="hidden md:flex items-center gap-4">
         <div className="h-[1px] w-12 bg-gradient-to-r from-transparent via-slate-500/20 to-transparent" />
         <span className="text-[10px] font-mono text-slate-500 uppercase tracking-widest animate-pulse">
           Données_Stream_Synchronisées
         </span>
         <div className="h-[1px] w-12 bg-gradient-to-r from-transparent via-slate-500/20 to-transparent" />
      </div>

      {/* ═══ DROITE : Titre & Meta ═══ */}
      <div className={`flex items-center gap-4 h-full px-6 border-l transition-colors ${
        darkMode ? 'border-white/5' : 'border-blue-100'
      }`}>
        {activeTitle ? (
          <motion.div 
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-4"
          >
            <div className="flex flex-col items-end">
              <span className="text-[9px] font-mono text-slate-500 uppercase tracking-tighter opacity-70">Ressource_Focus</span>
              <span className={`text-[10px] font-black uppercase tracking-wider ${
                darkMode ? 'text-white/90' : 'text-slate-800'
              }`}>
                {activeTitle}
              </span>
            </div>

            {/* Indicateur de type technique */}
            {activeItem?.type && (
              <div className={`px-2 py-0.5 rounded-sm border text-[8px] font-mono font-bold uppercase tracking-widest ${
                activeItem.type === 'evaluation' 
                  ? 'border-purple-500/40 text-purple-400 bg-purple-500/5' 
                  : 'border-cyan-500/40 text-cyan-400 bg-cyan-500/5'
              }`}>
                {activeItem.type === 'evaluation' ? 'Task_Quiz' : 'Task_Lab'}
              </div>
            )}
          </motion.div>
        ) : (
          <span className="text-[8px] font-mono text-slate-600 uppercase tracking-[0.2em] italic">
            En_Attente_Entrée...
          </span>
        )}
      </div>      
    </div>
  );
};

export default WorkspaceHeader;